import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLocalStorage } from '@frontend/hooks';

import type {
  NotificationStatusFilter,
  NotificationsUrlParams,
  NotificationViewMode,
} from '../types';
import { isTimePreset } from '../utils';

const DEFAULT_PAGE_SIZE = 20;
const MIN_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 100;
const PAGE_SIZE_STORAGE_KEY = 'notifications-pageSize';

const RESETS_PAGE: Array<keyof NotificationsUrlParams> = [
  'status',
  'agentId',
  'category',
  'priority',
  'time',
];

export function useNotificationsUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedPageSize, setStoredPageSize] = useLocalStorage(
    PAGE_SIZE_STORAGE_KEY,
    DEFAULT_PAGE_SIZE
  );

  const params: NotificationsUrlParams = useMemo(() => {
    const rawPage = parseInt(searchParams.get('page') || '1', 10);
    const rawPageSize = parseInt(searchParams.get('pageSize') || storedPageSize.toString(), 10);
    const rawTime = searchParams.get('time');

    return {
      page: Math.max(1, isNaN(rawPage) ? 1 : rawPage),
      pageSize: isNaN(rawPageSize)
        ? DEFAULT_PAGE_SIZE
        : Math.min(Math.max(MIN_PAGE_SIZE, rawPageSize), MAX_PAGE_SIZE),
      status: (searchParams.get('status') as NotificationStatusFilter) || 'all',
      agentId: searchParams.get('agentId') || undefined,
      category: searchParams.get('category') || undefined,
      priority: searchParams.get('priority') || undefined,
      // Drop unrecognized time presets so downstream consumers (helper, fetcher)
      // can trust the `TimePreset | undefined` shape.
      time: isTimePreset(rawTime) ? rawTime : undefined,
      view: (searchParams.get('view') as NotificationViewMode) || 'table',
    };
  }, [searchParams, storedPageSize]);

  const updateParams = useCallback(
    (updates: Partial<NotificationsUrlParams>) => {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);

          if ('pageSize' in updates && updates.pageSize !== undefined) {
            setStoredPageSize(updates.pageSize);
          }

          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === '' || value === null) {
              newParams.delete(key);
            } else {
              newParams.set(key, value.toString());
            }
          });

          // Reset page to 1 when filters change
          if (!('page' in updates) && RESETS_PAGE.some(k => k in updates)) {
            newParams.set('page', '1');
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, setStoredPageSize]
  );

  return { params, updateParams };
}
